import type { DateTime } from "luxon";
import type { CostDeepDiveResponse } from "../../../utils/models";
import { type ClampRange, formatDateKey } from "../utils";
import type { CostDayRow } from "./types";

export const buildCostDayRows = (
	data: CostDeepDiveResponse | undefined,
	timezone: string,
	dateRange?: { start: DateTime; end: DateTime },
	// Selected range used to clamp weekly bucket labels so an edge week never displays a date outside the selection.
	clampRange?: ClampRange,
): CostDayRow[] => {
	if (!data) return [];

	const makeRow = (dateStr: string): CostDayRow => ({
		date: dateStr,
		dateDisplay: formatDateKey(dateStr, timezone, clampRange),
		baseCost: data.baseCost[dateStr] ?? 0,
		dailyOtCost: data.dailyOtCost[dateStr] ?? 0,
		weeklyOtCost: data.weeklyOtCost[dateStr] ?? 0,
		consecutiveOtCost: data.consecutiveOtCost?.[dateStr] ?? 0,
		agencyCost: data.agencyCost[dateStr] ?? 0,
		bonusCost: data.bonusCost[dateStr] ?? 0,
		ptoCost: data.ptoCost[dateStr] ?? 0,
		totalCost: data.totalCost[dateStr] ?? 0,
		baseHours: data.baseHours?.[dateStr] ?? 0,
		dailyOtHours: data.dailyOtHours?.[dateStr] ?? 0,
		weeklyOtHours: data.weeklyOtHours?.[dateStr] ?? 0,
		consecutiveOtHours: data.consecutiveOtHours?.[dateStr] ?? 0,
		agencyHours: data.agencyHours?.[dateStr] ?? 0,
		totalHours: data.totalHours?.[dateStr] ?? 0,
	});

	if (dateRange) {
		const rows: CostDayRow[] = [];
		const end = dateRange.end.startOf("day");
		let cursor = dateRange.start.startOf("day");
		while (cursor <= end) {
			rows.push(makeRow(cursor.toFormat("yyyy-MM-dd")));
			cursor = cursor.plus({ days: 1 });
		}
		return rows;
	}

	const allDates = new Set([
		...Object.keys(data.baseCost),
		...Object.keys(data.dailyOtCost),
		...Object.keys(data.weeklyOtCost),
		...Object.keys(data.consecutiveOtCost ?? {}),
		...Object.keys(data.agencyCost),
		...Object.keys(data.bonusCost),
		...Object.keys(data.ptoCost),
		...Object.keys(data.totalCost),
		...Object.keys(data.baseHours ?? {}),
		...Object.keys(data.dailyOtHours ?? {}),
		...Object.keys(data.weeklyOtHours ?? {}),
		...Object.keys(data.consecutiveOtHours ?? {}),
		...Object.keys(data.agencyHours ?? {}),
		...Object.keys(data.totalHours ?? {}),
	]);
	return Array.from(allDates)
		.map(makeRow)
		.sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * DAT-289: whether the Consecutive OT column/series is rendered. Shared by the table and the chart so
 * they can never disagree, and exported so tests exercise the real rule rather than a copy of it.
 *
 * Keyed on the department CONFIGURING a rule, never on whether a premium landed in the visible range —
 * a 7th consecutive day is occasional, so a value-based gate would make the column blink in and out as
 * the user pages between weeks.
 */
export const showsConsecutiveOt = (data: CostDeepDiveResponse | undefined): boolean => data?.hasConsecutiveOtRule ?? false;

/** Format hours as a compact string like "12.5h" for tooltips/tables (no unit abbreviation for K). */
export const formatHours = (value: number, fractionDigits = 1): string => `${value.toFixed(fractionDigits)}h`;

/** Tick renderer for chart Y-axes when in hours mode. Mirrors `costToKDollars`. */
export const hoursToKHours = (value: number): string => {
	if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
	return `${Math.round(value)}`;
};
